import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import 'react-toastify/dist/ReactToastify.css';
import {
  fetchEditProfileService,
  fetchEditEmailService,
  fetchEditPhoneService,
  fetchChangePasswordService,
} from '../../services/Profile';
import {
  activateNotificationChannelService,
  confirmNotificationChannelService,
  fetchDriverSelectedNotificationTypeService,
  fetchNotificationTypesService,
} from '../../services/NotificationChannel';
import { ProfileStateType } from '../types';

const initialState: ProfileStateType = {
  profileLoading: false,
  driverId: 0,
  name: '',
  cellphone: '',
  email: '',
  birth: '',
  firstTicket: '',
  picture: '',
  termsOfUseDateTime: '',
  passwordUpdateDateTime: '',
  cpf: '',
  editLoading: false,
  editEmailIsOpen: false,
  emailEditStep: 1,
  editPhoneIsOpen: false,
  phoneEditStep: 1,
  changePasswordIsOpen: false,
  editCPFIsOpen: false,
  editCPFLoading: false,
  notificationTypes: [],
  activeNotificationTypeId: null,
  notificationChannelLoading: false,
  notificationChannelModalIsOpen: false,
  pendingNotificationTypeId: null,
  notificationChannelDestination: '',
};

export const fetchEditProfile = createAsyncThunk(
  'editProfile',
  async (params: { driverId: string; name: string; birth: string }) => {
    return await fetchEditProfileService(params);
  },
);

export const fetchEditEmail = createAsyncThunk(
  'editEmail',
  async (params: {
    driverId: string;
    email: string;
    validationCode: string;
  }) => {
    return await fetchEditEmailService(params);
  },
);

export const fetchEditPhone = createAsyncThunk(
  'editPhone',
  async (params: {
    driverId: string;
    cellphone: string;
    validationCode: string;
  }) => {
    return await fetchEditPhoneService(params);
  },
);

export const fetchChangePassword = createAsyncThunk(
  'changePassword',
  async (params: {
    driverId: string;
    oldPassword: string;
    newPassword: string;
  }) => {
    return await fetchChangePasswordService(params);
  },
);

export const fetchNotificationTypes = createAsyncThunk(
  'fetchNotificationTypes',
  async (params: { driverId: string }) => {
    return await fetchNotificationTypesService(params);
  },
);

export const fetchDriverSelectedNotificationType = createAsyncThunk(
  'fetchDriverSelectedNotificationType',
  async (params: { driverId: string }) => {
    return await fetchDriverSelectedNotificationTypeService(params);
  },
);

export const activateNotificationChannel = createAsyncThunk(
  'activateNotificationChannel',
  async (params: { driverId: string; notificationType: number }) => {
    return await activateNotificationChannelService(params);
  },
);

export const confirmNotificationChannel = createAsyncThunk(
  'confirmNotificationChannel',
  async (params: {
    driverId: string;
    notificationType: number;
    code: string;
  }) => {
    return await confirmNotificationChannelService(params);
  },
);

export const profileSlice = createSlice({
  name: 'profile',
  initialState,
  reducers: {
    toggleProfileLoading(state) {
      state.profileLoading = !state.profileLoading;
    },
    setUser: (
      state,
      action: PayloadAction<{
        driverId: number;
        name: string;
        cellphone: string;
        email: string;
        birth: string;
        firstTicket: string;
        picture: string;
        termsOfUseDateTime: string;
        passwordUpdateDateTime: string;
      }>,
    ) => {
      return {
        ...state,
        driverId: action.payload.driverId,
        name: action.payload.name,
        cellphone: action.payload.cellphone,
        email: action.payload.email,
        birth: action.payload.birth,
        firstTicket: action.payload.firstTicket,
        picture: action.payload.picture,
        termsOfUseDateTime: action.payload.termsOfUseDateTime,
        passwordUpdateDateTime: action.payload.passwordUpdateDateTime,
      };
    },
    setCPF: (state, action: PayloadAction<string>) => {
      return {
        ...state,
        cpf: action.payload,
      };
    },
    toggleEditEmailIsOpen: (state) => {
      return {
        ...state,
        editEmailIsOpen: !state.editEmailIsOpen,
      };
    },
    setEmailEditStep: (state, action: PayloadAction<number>) => {
      return {
        ...state,
        emailEditStep: action.payload,
      };
    },
    toggleEditPhoneIsOpen: (state) => {
      return {
        ...state,
        editPhoneIsOpen: !state.editPhoneIsOpen,
      };
    },
    setPhoneEditStep: (state, action: PayloadAction<number>) => {
      return {
        ...state,
        phoneEditStep: action.payload,
      };
    },
    toggleChangePasswordIsOpen: (state) => {
      return {
        ...state,
        changePasswordIsOpen: !state.changePasswordIsOpen,
      };
    },
    toggleEditCPFProfileIsOpen(state) {
      return {
        ...state,
        editCPFIsOpen: !state.editCPFIsOpen,
      };
    },
    toggleEditCPFProfileLoading(state) {
      return {
        ...state,
        editCPFLoading: !state.editCPFLoading,
      };
    },
    closeNotificationChannelModal(state) {
      return {
        ...state,
        notificationChannelModalIsOpen: false,
        pendingNotificationTypeId: null,
        notificationChannelDestination: '',
      };
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchEditProfile.pending, (state) => {
      return {
        ...state,
        profileLoading: true,
      };
    }),
      builder.addCase(fetchEditProfile.fulfilled, (state, res) => {
        return {
          ...state,
          driverId: res.payload.data.id,
          name: res.payload.data.name,
          birth: res.payload.data.birth,
          cellphone: res.payload.data.cellphone,
          email: res.payload.data.email,
          profileLoading: false,
        };
      }),
      builder.addCase(fetchEditProfile.rejected, (state, res) => {
        return {
          ...state,
          profileLoading: false,
        };
      });

    builder.addCase(fetchEditEmail.pending, (state) => {
      return {
        ...state,
        profileLoading: true,
      };
    }),
      builder.addCase(fetchEditEmail.fulfilled, (state, res) => {
        return {
          ...state,
          email: res.payload.data.email,
          profileLoading: false,
        };
      }),
      builder.addCase(fetchEditEmail.rejected, (state, res) => {
        return {
          ...state,
          profileLoading: false,
        };
      });

    builder.addCase(fetchEditPhone.pending, (state) => {
      return {
        ...state,
        profileLoading: true,
      };
    }),
      builder.addCase(fetchEditPhone.fulfilled, (state, res) => {
        return {
          ...state,
          cellphone: res.payload.data.cellphone,
          profileLoading: false,
        };
      }),
      builder.addCase(fetchEditPhone.rejected, (state, res) => {
        return {
          ...state,
          profileLoading: false,
        };
      });

    builder.addCase(fetchChangePassword.pending, (state) => {
      return {
        ...state,
        profileLoading: true,
      };
    }),
      builder.addCase(fetchChangePassword.fulfilled, (state, res) => {
        return {
          ...state,
          profileLoading: false,
        };
      }),
      builder.addCase(fetchChangePassword.rejected, (state, res) => {
        return {
          ...state,
          profileLoading: false,
        };
      });

    builder.addCase(fetchNotificationTypes.pending, (state) => {
      return {
        ...state,
        notificationChannelLoading: true,
      };
    }),
      builder.addCase(fetchNotificationTypes.fulfilled, (state, res) => {
        return {
          ...state,
          notificationTypes: res.payload.data ?? [],
          notificationChannelLoading: false,
        };
      }),
      builder.addCase(fetchNotificationTypes.rejected, (state) => {
        return {
          ...state,
          notificationChannelLoading: false,
        };
      });

    builder.addCase(fetchDriverSelectedNotificationType.fulfilled, (state, res) => {
      return {
        ...state,
        activeNotificationTypeId: res.payload.data?.typeId ?? null,
      };
    });

    builder.addCase(activateNotificationChannel.pending, (state, action) => {
      return {
        ...state,
        notificationChannelLoading: true,
        pendingNotificationTypeId: action.meta.arg.notificationType,
      };
    }),
      builder.addCase(activateNotificationChannel.fulfilled, (state, res) => {
        const destination =
          res.payload.data.email || res.payload.data.number || '';
        return {
          ...state,
          notificationChannelLoading: false,
          notificationChannelModalIsOpen: true,
          notificationChannelDestination: destination,
        };
      }),
      builder.addCase(activateNotificationChannel.rejected, (state) => {
        return {
          ...state,
          notificationChannelLoading: false,
          pendingNotificationTypeId: null,
          notificationChannelDestination: '',
        };
      });

    builder.addCase(confirmNotificationChannel.pending, (state) => {
      return {
        ...state,
        notificationChannelLoading: true,
      };
    }),
      builder.addCase(confirmNotificationChannel.fulfilled, (state, res) => {
        return {
          ...state,
          activeNotificationTypeId: res.payload.data.typeId,
          notificationChannelLoading: false,
          notificationChannelModalIsOpen: false,
          pendingNotificationTypeId: null,
          notificationChannelDestination: '',
        };
      }),
      builder.addCase(confirmNotificationChannel.rejected, (state) => {
        return {
          ...state,
          notificationChannelLoading: false,
        };
      });
  },
});

// Action creators are generated for each case reducer function
export const {
  toggleProfileLoading,
  setUser,
  setCPF,
  toggleEditEmailIsOpen,
  setEmailEditStep,
  toggleEditPhoneIsOpen,
  setPhoneEditStep,
  toggleChangePasswordIsOpen,
  toggleEditCPFProfileIsOpen,
  toggleEditCPFProfileLoading,
  closeNotificationChannelModal,
} = profileSlice.actions;
